import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { RootContainerCfgUIElement } from '@/elements/containers/RootContainer/RootContainer';
import { MixedContainerCfgUIElement } from '@/elements/containers/MixedContainer/MixedContainer';
import { ComponentItemsContainerCfgUIElement } from '@/elements/containers/ComponentItemsContainer/ComponentItemsContainer';
import { ProductsContainerCfgUIElement } from '@/elements/containers/ProductsContainer/ProductsContainer';
import {
  createMockTextAttributeChild,
  createMockBooleanAttributeChild,
  createMockIntegerAttributeChild,
  createMockComponentChild,
  createMockContainerChild,
  createMockCompositeProductInfoChild,
  createMockComponentStateForMerge,
} from '../mocks';
import {
  mockRootContainer,
  mockCompositeProductInfo,
  mockComponentItemsContainerFull,
  mockMixedContainerFull,
  mockNestedComponentItemsFull,
  mockProductMergeContainerFull,
  mockCoffeeMachineComponent,
  mockAccessoryComponent,
  mockColorAttributeConfig,
  mockSizeAttributeConfig,
  mockToggleAttributeConfig,
  mockQuantityAttributeConfig,
  mockMergeComponent1Config,
  mockMergeComponent2Config,
} from '../mockData';
import styles from './StyleGuideFullExample.module.css';

export const StyleGuideFullExample: React.FC = observer(function StyleGuideFullExample() {
  // ============================================================================
  // 1. CompositeProductInfo (product header with title + description)
  // ============================================================================
  const compositeProductInfo = useMemo(() => createMockCompositeProductInfoChild(mockCompositeProductInfo), []);

  // ============================================================================
  // 2. ComponentItemsContainer (component + 1 attribute, all showTitle/showDescription on)
  // ============================================================================
  const componentItemsChildren = useMemo(
    () => [
      createMockComponentChild(mockCoffeeMachineComponent),
      createMockTextAttributeChild(mockColorAttributeConfig),
    ],
    [],
  );

  const componentItemsContainer = useMemo(
    () =>
      createMockContainerChild(
        ComponentItemsContainerCfgUIElement,
        mockComponentItemsContainerFull,
        componentItemsChildren,
        { level: 2, parentLevel: 1 }, // Direct child of root (level 1) = level 2
      ),
    [componentItemsChildren],
  );

  // ============================================================================
  // 3. MixedContainer (component, product, attributes, nested ComponentItemsContainer)
  // ============================================================================

  // Nested ComponentItemsContainer inside MixedContainer
  const nestedComponentItemsChildren = useMemo(
    () => [
      createMockComponentChild(mockAccessoryComponent),
      createMockTextAttributeChild(mockSizeAttributeConfig),
      createMockBooleanAttributeChild(mockToggleAttributeConfig),
    ],
    [],
  );

  const nestedComponentItemsContainer = useMemo(
    () =>
      createMockContainerChild(
        ComponentItemsContainerCfgUIElement,
        mockNestedComponentItemsFull,
        nestedComponentItemsChildren,
        { level: 3, parentLevel: 2 }, // Nested inside level 2 container = level 3
      ),
    [nestedComponentItemsChildren],
  );

  // MixedContainer children: component, product, attributes, nested container
  const mixedContainerChildren = useMemo(
    () => [
      createMockComponentChild({
        ...mockCoffeeMachineComponent,
        viewElement: {
          ...mockCoffeeMachineComponent.viewElement,
          id: 'mixed-component',
        },
      }),
      createMockTextAttributeChild({
        ...mockSizeAttributeConfig,
        id: 'mixed-size-attr',
        viewElement: { ...mockSizeAttributeConfig.viewElement, id: 'mixed-size-attr' },
      }),
      createMockIntegerAttributeChild(mockQuantityAttributeConfig),
      nestedComponentItemsContainer,
    ],
    [nestedComponentItemsContainer],
  );

  const mixedContainer = useMemo(
    () =>
      createMockContainerChild(
        MixedContainerCfgUIElement,
        mockMixedContainerFull,
        mixedContainerChildren,
        { level: 2, parentLevel: 1 }, // Direct child of root (level 1) = level 2
      ),
    [mixedContainerChildren],
  );

  // ============================================================================
  // 4. ProductMergeContainer (products from 2 components)
  // Uses real ComponentElementState instances for instanceof checks
  // ============================================================================
  const mergeComponent1 = useMemo(() => createMockComponentStateForMerge(mockMergeComponent1Config), []);
  const mergeComponent2 = useMemo(() => createMockComponentStateForMerge(mockMergeComponent2Config), []);

  const productMergeContainer = useMemo(
    () =>
      createMockContainerChild(
        ProductsContainerCfgUIElement,
        mockProductMergeContainerFull,
        [mergeComponent1, mergeComponent2],
        { level: 2, parentLevel: 1 }, // Direct child of root (level 1) = level 2
      ),
    [mergeComponent1, mergeComponent2],
  );

  // ============================================================================
  // Root Container with all children
  // ============================================================================
  const rootChildren = useMemo(
    () => [compositeProductInfo, componentItemsContainer, mixedContainer, productMergeContainer],
    [compositeProductInfo, componentItemsContainer, mixedContainer, productMergeContainer],
  );

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Full Configurator Example</h2>
      <p className={styles.description}>
        Complete nested structure demonstrating all container and element types working together.
      </p>

      <div className={styles.demo}>
        <RootContainerCfgUIElement viewElement={mockRootContainer} childrenItems={rootChildren} />
      </div>

      <h3 className={styles.legend}>Structure Legend:</h3>
      <pre className={styles.structureTree}>
        {`Root Container
├── CompositeProductInfo (title + description)
├── ComponentItems Container: "Main Component"
│   ├── Component Switch (showTitle + showDescription)
│   └── Text Attribute: Color (showTitle + showDescription)
├── Mixed Container: "Configuration Options"
│   ├── Component Switch: Coffee Machine
│   ├── Text Attribute: Size
│   ├── Integer Attribute: Quantity
│   └── ComponentItems Container: "Accessory Settings" (nested)
│       ├── Component Switch: Accessory
│       ├── Text Attribute: Size
│       └── Boolean Attribute: Warranty
└── Products Container: "Select Model"
    └── Merged Products (4 products from 2 components)`}
      </pre>
    </div>
  );
});
